import React from "react";
import { Link } from "react-router-dom";
import { Shield, AlertTriangle, Users, Lock, Phone, Scale } from "lucide-react";

const SafetyStandardsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/images/logo-main.png" 
              alt="RecoveryMatch" 
              className="w-10 h-10 rounded-xl"
            />
            <span className="text-xl font-bold text-[#1C3A7A]">RecoveryMatch</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          {/* Title Section */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1C3A7A] rounded-full mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#1C3A7A] mb-2">
              Safety Standards
            </h1>
            <p className="text-gray-500">
              Our commitment to preventing child sexual abuse and exploitation (CSAE)
            </p>
            <p className="text-sm text-gray-400 mt-2">Last Updated: March 2025</p>
          </div>

          {/* Zero Tolerance Section */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-xl font-bold text-gray-900">Zero Tolerance Policy</h2>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <p className="text-gray-700 leading-relaxed">
                RecoveryMatch maintains a <strong>zero tolerance policy</strong> for any content, behavior, or activity related to child sexual abuse and exploitation (CSAE). This includes, but is not limited to:
              </p>
              <ul className="mt-4 space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Child sexual abuse material (CSAM) of any kind
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Grooming or solicitation of minors
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Sexualized content involving minors
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Any attempt to contact, meet, or exploit minors
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Sextortion or trafficking of minors
                </li>
              </ul>
            </div>
          </section>

          {/* Age Verification Section */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-[#1C3A7A]" />
              <h2 className="text-xl font-bold text-gray-900">Age Requirements</h2>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                RecoveryMatch is a dating platform exclusively for adults aged <strong>18 years and older</strong>. We enforce this requirement through:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  Mandatory age confirmation during registration
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  Date of birth verification
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  Immediate account termination for users found to be underage
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  Profile review and moderation systems
                </li>
              </ul>
            </div>
          </section>

          {/* Content Moderation Section */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-6 h-6 text-[#1C3A7A]" />
              <h2 className="text-xl font-bold text-gray-900">Content Moderation</h2>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                We employ multiple layers of content moderation to detect and prevent CSAE:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  Automated scanning of uploaded images and content
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  Human review of flagged content and reports
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  User reporting mechanisms for suspicious activity
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  Regular audits of platform content and user behavior
                </li>
              </ul>
            </div>
          </section>

          {/* Reporting Section */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Phone className="w-6 h-6 text-[#1C3A7A]" />
              <h2 className="text-xl font-bold text-gray-900">Reporting Mechanisms</h2>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                If you encounter any CSAE content or suspicious behavior, please report it immediately:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  <span><strong>In-App:</strong> Use the "Report" button on any profile or message</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  <span><strong>Email:</strong> <a href="mailto:support@recoverymatch.com" className="text-[#1C3A7A] underline">support@recoverymatch.com</a></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  <span><strong>NCMEC CyberTipline:</strong> <a href="https://www.missingkids.org/gethelpnow/cybertipline" target="_blank" rel="noopener noreferrer" className="text-[#1C3A7A] underline">Report to NCMEC</a></span>
                </li>
              </ul>
              <p className="text-gray-600 mt-4 text-sm">
                All reports are reviewed promptly and treated with the utmost seriousness.
              </p>
            </div>
          </section>

          {/* Law Enforcement Section */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="w-6 h-6 text-[#1C3A7A]" />
              <h2 className="text-xl font-bold text-gray-900">Law Enforcement Cooperation</h2>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                RecoveryMatch is committed to full cooperation with law enforcement agencies:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  We report all identified CSAM to the National Center for Missing & Exploited Children (NCMEC)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  We respond promptly to valid legal requests from law enforcement
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  We preserve evidence and account data as required by law
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1C3A7A] font-bold">•</span>
                  We actively participate in industry efforts to combat CSAE
                </li>
              </ul>
            </div>
          </section>

          {/* Enforcement Actions Section */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-[#1C3A7A]" />
              <h2 className="text-xl font-bold text-gray-900">Enforcement Actions</h2>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                Violations of our CSAE policy result in immediate and permanent action:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Immediate account termination without warning
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Permanent ban from the platform
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Reporting to relevant law enforcement authorities
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Reporting to NCMEC as required by law
                </li>
              </ul>
            </div>
          </section>

          {/* Contact Section */}
          <section className="bg-[#1C3A7A] rounded-xl p-6 text-center">
            <h2 className="text-xl font-bold text-white mb-2">Questions or Concerns?</h2>
            <p className="text-white/80 mb-4">
              For any questions about our safety standards, please contact us.
            </p>
            <a 
              href="mailto:support@recoverymatch.com" 
              className="inline-block bg-white text-[#1C3A7A] font-semibold px-6 py-3 rounded-full hover:bg-gray-100 transition-colors"
            >
              Contact Safety Team
            </a>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <Link to="/privacy" className="hover:text-[#1C3A7A]">Privacy Policy</Link>
          <span className="mx-2">•</span>
          <Link to="/terms" className="hover:text-[#1C3A7A]">Terms of Service</Link>
          <span className="mx-2">•</span>
          <Link to="/safety" className="hover:text-[#1C3A7A]">Safety Tips</Link>
        </div>
      </main>
    </div>
  );
};

export default SafetyStandardsPage;
